'use client';

import CheckCircle from '@/components/icon/check-circle';
import bengaliNumberFormatter from '@/utils/bn';
import { TimeZoneUtils } from '@/utils/timezone-utils';
import { PromoType, PromoViewModel } from '@hidayahlab/academy-suite-corelib';
import { Button } from '@tajdid/tajdid-ui';
import {
  ArrowRightIcon,
  CheckV2Icon,
  StopWatchIconIcon,
  XCircleIcon,
} from '@tajdid/tajdid-ui/icons';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import CountdownTimer from '../count-down-timer';

type PromoMessageProps = {
  promoData?: PromoViewModel;
};

export default function PromoMessageDetails({ promoData }: PromoMessageProps) {
  const [show, setShow] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isPromoApplied = searchParams.get('promo');

  if (!promoData || !show) {
    return;
  }

  const promoUrl = `${pathname}?promo=${promoData.code}#course_card`;
  const secondsLeft = TimeZoneUtils.calculatePromoTimeLeft(
    promoData.expirationDate,
  );

  return (
    <div>
      <div className="text-warning-[#4f1802] bg-warning-300">
        <div className="flex items-center justify-center container lg:w-[1190px] mx-auto">
          <div className="hidden mx-auto md:flex">
            <div className="flex gap-0 items-center md:gap-2 0">
              <CheckV2Icon width={24} height={24} className="inline" />

              <p className="text-sm lg:text-base">
                <span className="font-semibold">{promoData.code}</span> প্রোমো
                এপ্লাই করলে{' '}
                <span className="font-semibold">
                  {promoData.type === PromoType.percentage
                    ? `${bengaliNumberFormatter(promoData.value)}%`
                    : `${bengaliNumberFormatter(promoData.value)} টাকা`}{' '}
                  ডিসকাউন্ট
                </span>{' '}
                আর বাকি
              </p>
              <div className="flex gap-2 items-center">
                <StopWatchIconIcon height={20} width={20} />
                <CountdownTimer secondsLeft={secondsLeft} />
                <Button
                  variant={isPromoApplied ? 'outlined' : 'contained'}
                  icon={isPromoApplied ? <CheckCircle /> : <ArrowRightIcon />}
                  iconPosition="end"
                  onClick={() => router.push(promoUrl)}
                  size="small"
                  className="bg-warning-600 hover:bg-warning-700"
                >
                  {isPromoApplied ? 'প্রোমো এপ্লাইড' : 'এপ্লাই করুন'}
                </Button>
              </div>
            </div>
          </div>
          <Button
            variant="icon"
            className="hidden relative bg-transparent hover:bg-transparent md:inline-flex"
            onClick={() => setShow(false)}
          >
            <XCircleIcon
              className="text-warning-600 hover:text-warning-700"
              height={32}
              width={32}
            />
          </Button>
        </div>
      </div>
      <div className="text-xs fixed bottom-[111px] flex justify-center -z-50 text-warning-[#4f1802] bg-warning-300 w-full md:hidden items-center shadow-sm">
        <Button
          variant="link"
          className="px-1 text-xs text-warning-[#4f1802] font-normal"
          onClick={() => router.push(promoUrl)}
        >
          <div className="items-center py-2 space-x-1 flex-inline">
            <span className="font-semibold">
              {promoData.type === PromoType.percentage
                ? `${bengaliNumberFormatter(promoData.value)}%`
                : `${bengaliNumberFormatter(promoData.value)} টাকা`}{' '}
            </span>
            <span>ডিসকাউন্ট পেতে</span>
            <span className="font-semibold">{promoData.code}</span>
            <span>প্রোমো</span>
            <span className="ml-1 bg-warning-600 rounded-[4px] p-[3px] text-white">
              এপ্লাই {` `}করুন
            </span>
            <StopWatchIconIcon
              height={16}
              width={16}
              style={{ marginRight: '-4px' }}
              className="inline font-semibold text-warning-[#4f1802]"
            />
            <CountdownTimer secondsLeft={secondsLeft} />
          </div>
        </Button>
      </div>
    </div>
  );
}
